import React, { Component, ErrorInfo, ReactNode } from "react";
import { logger } from "./logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component with automatic error logging
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error
    logger.errorBoundary(error, errorInfo, {
      component: "ErrorBoundary",
      method: "componentDidCatch",
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI or default error UI
      return (
        this.props.fallback || (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h2>Something went wrong.</h2>
            <p>An error occurred while rendering this component.</p>
            <button
              onClick={() =>
                this.setState({ hasError: false, error: undefined })
              }
              style={{ padding: "10px 20px", marginTop: "10px" }}
            >
              Try again
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
